/**
 * Super-admin v2 sidebar — 240px dark with gold accent (from mock).
 */
import type { ReactNode } from "react";
import { LayoutDashboard, Store, Briefcase, Wallet, TrendingUp, Settings, LogOut, Megaphone, BookOpen } from "lucide-react";
import { C, FONT } from "../styles/theme";

export type Section = "dashboard" | "cafes" | "crm" | "finance" | "analytics" | "news" | "library" | "settings";

interface MenuItem {
  id: Section;
  icon: ReactNode;
  label: string;
  badge?: number;
}

interface Props {
  section: Section;
  setSection: (s: Section) => void;
  userEmail: string;
  alertsCount?: number;
  crmHotCount?: number;
  onLogout?: () => void;
  /** Мобильный drawer (≤768px): открыт/закрыт. На десктопе игнорируется. */
  mobileOpen?: boolean;
}

export function Sidebar({ section, setSection, userEmail, alertsCount = 0, crmHotCount = 0, onLogout, mobileOpen = false }: Props) {
  const items: MenuItem[] = [
    { id: "dashboard", icon: <LayoutDashboard size={18} />, label: "Dashboard" },
    { id: "cafes",     icon: <Store size={18} />, label: "Кафе", badge: alertsCount },
    { id: "crm",       icon: <Briefcase size={18} />, label: "CRM", badge: crmHotCount },
    { id: "finance",   icon: <Wallet size={18} />, label: "Финансы" },
    { id: "analytics", icon: <TrendingUp size={18} />, label: "Аналитика" },
    { id: "news",      icon: <Megaphone size={18} />, label: "Новости" },
    { id: "library",   icon: <BookOpen size={18} />, label: "База блюд" },
    { id: "settings",  icon: <Settings size={18} />, label: "Настройки" },
  ];

  const initial = (userEmail[0] ?? "E").toUpperCase();

  return (
    <div className={"sa-sidebar" + (mobileOpen ? " open" : "")} style={{
      width: 240, background: "#0A0E14",
      borderRight: `1px solid ${C.border}`,
      display: "flex", flexDirection: "column",
    }}>
      {/* Logo */}
      <div style={{
        padding: "24px 20px", borderBottom: `1px solid ${C.border}`,
        display: "flex", alignItems: "center", gap: 12,
      }}>
        <div style={{
          width: 40, height: 40, borderRadius: 10,
          background: `linear-gradient(135deg, ${C.brand}, ${C.brandDark})`,
          color: "#0A0E14",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 18, fontWeight: 800, fontFamily: FONT.display,
        }}>e</div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.text, fontFamily: FONT.display }}>e-menu.bar</div>
          <div style={{ fontSize: 10, color: C.muted, letterSpacing: 1, textTransform: "uppercase" }}>Super Admin</div>
        </div>
      </div>

      {/* Menu */}
      <div style={{ flex: 1, padding: "16px 12px", display: "flex", flexDirection: "column", gap: 4 }}>
        {items.map((item) => {
          const isActive = section === item.id;
          return (
            <button key={item.id} onClick={() => setSection(item.id)} style={{
              background: isActive ? C.brandSoft : "transparent",
              border: "none",
              borderRadius: 10,
              padding: "10px 14px",
              display: "flex", alignItems: "center", gap: 12,
              color: isActive ? C.brand : C.textSoft,
              fontSize: 13, fontWeight: isActive ? 700 : 500,
              cursor: "pointer", textAlign: "left", position: "relative",
              borderLeft: isActive ? `3px solid ${C.brand}` : "3px solid transparent",
              paddingLeft: isActive ? 11 : 14,
              fontFamily: FONT.body,
            }}>
              <span style={{ display: "flex", alignItems: "center" }}>{item.icon}</span>
              <span style={{ flex: 1 }}>{item.label}</span>
              {item.badge && item.badge > 0 ? (
                <span style={{
                  background: C.red, color: "white", fontSize: 10, fontWeight: 700,
                  padding: "1px 6px", borderRadius: 100, minWidth: 18, textAlign: "center",
                }}>{item.badge}</span>
              ) : null}
            </button>
          );
        })}
      </div>

      {/* Footer user */}
      <div style={{
        padding: "16px 20px", borderTop: `1px solid ${C.border}`,
        display: "flex", alignItems: "center", gap: 10,
      }}>
        <div style={{
          width: 36, height: 36, borderRadius: "50%",
          background: `linear-gradient(135deg, ${C.brand}, ${C.brandDark})`,
          color: "#0A0E14",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 14, fontWeight: 800, fontFamily: FONT.display,
        }}>{initial}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 12, fontWeight: 700, color: C.text,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>{userEmail || "Super Admin"}</div>
          <div style={{ fontSize: 10, color: C.muted }}>Founder</div>
        </div>
        {onLogout && (
          <button
            onClick={onLogout}
            title="Выйти"
            style={{
              background: "transparent", border: "none", color: C.muted,
              cursor: "pointer", padding: 4,
              display: "flex", alignItems: "center",
            }}
          ><LogOut size={16} /></button>
        )}
      </div>
    </div>
  );
}
